import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';

import type { PublicSignerProgressSaveErrorDisposition } from '../publicSignerError';

export const PUBLIC_SIGNER_AUTOSAVE_INTERVAL_MS = 5000;

type PublicSignerAutosaveOptions<TSnapshot> = {
  classifyError?: (error: unknown) => PublicSignerProgressSaveErrorDisposition;
  enabled: boolean;
  getSnapshot: () => TSnapshot | null;
  intervalMs?: number;
  saveSnapshot: (snapshot: TSnapshot) => Promise<unknown>;
  sessionKey: string | null;
};

type PublicSignerAutosaveStatus = 'idle' | 'saved' | 'saving';

export function usePublicSignerAutosave<TSnapshot>({
  classifyError = () => 'retryable',
  enabled,
  getSnapshot,
  intervalMs = PUBLIC_SIGNER_AUTOSAVE_INTERVAL_MS,
  saveSnapshot,
  sessionKey
}: PublicSignerAutosaveOptions<TSnapshot>) {
  const [saveErrorDisposition, setSaveErrorDisposition] =
    useState<PublicSignerProgressSaveErrorDisposition | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [lastError, setLastError] = useState<unknown>(null);
  const [saveStatus, setSaveStatus] =
    useState<PublicSignerAutosaveStatus>('idle');
  const activeSessionKeyRef = useRef<string | null>(sessionKey);
  const blockedErrorDispositionRef = useRef<Exclude<
    PublicSignerProgressSaveErrorDisposition,
    'retryable'
  > | null>(null);
  const classifyErrorRef = useRef(classifyError);
  const enabledRef = useRef(enabled);
  const flushAfterCurrentRef = useRef(false);
  // Prevents stale saves from updating a new session.
  const generationRef = useRef(0);
  const getSnapshotRef = useRef(getSnapshot);
  const inFlightRef = useRef<Promise<boolean> | null>(null);
  const lastSavedRevisionRef = useRef(0);
  const pausedRef = useRef(false);
  const revisionRef = useRef(0);
  const saveSnapshotRef = useRef(saveSnapshot);
  const savingRevisionRef = useRef<number | null>(null);

  classifyErrorRef.current = classifyError;
  enabledRef.current = enabled;
  getSnapshotRef.current = getSnapshot;
  saveSnapshotRef.current = saveSnapshot;

  useLayoutEffect(() => {
    if (activeSessionKeyRef.current === sessionKey) {
      return;
    }

    activeSessionKeyRef.current = sessionKey;
    blockedErrorDispositionRef.current = null;
    flushAfterCurrentRef.current = false;
    generationRef.current += 1;
    inFlightRef.current = null;
    lastSavedRevisionRef.current = 0;
    pausedRef.current = false;
    revisionRef.current = 0;
    savingRevisionRef.current = null;
    setSaveErrorDisposition(null);
    setIsSaving(false);
    setLastError(null);
    setSaveStatus('idle');
  }, [sessionKey]);

  const flushNow = useCallback((): Promise<boolean> => {
    if (
      !enabledRef.current ||
      !activeSessionKeyRef.current ||
      blockedErrorDispositionRef.current ||
      pausedRef.current ||
      revisionRef.current <= lastSavedRevisionRef.current
    ) {
      return Promise.resolve(true);
    }

    // Coalesce overlapping flushes and queue one follow-up for newer edits.
    if (inFlightRef.current) {
      if (
        savingRevisionRef.current !== null &&
        revisionRef.current > savingRevisionRef.current
      ) {
        flushAfterCurrentRef.current = true;
      }
      return inFlightRef.current;
    }

    const snapshot = getSnapshotRef.current();
    if (snapshot === null) {
      return Promise.resolve(false);
    }

    const generation = generationRef.current;
    const revision = revisionRef.current;
    savingRevisionRef.current = revision;
    setIsSaving(true);

    const savePromise = (async () => {
      try {
        await Promise.resolve().then(() => saveSnapshotRef.current(snapshot));

        if (generation === generationRef.current) {
          lastSavedRevisionRef.current = Math.max(
            lastSavedRevisionRef.current,
            revision
          );
          setSaveStatus(
            revisionRef.current > lastSavedRevisionRef.current
              ? 'saving'
              : 'saved'
          );
          blockedErrorDispositionRef.current = null;
          setSaveErrorDisposition(null);
          setLastError(null);
        }
        return true;
      } catch (error) {
        if (generation === generationRef.current) {
          const disposition = classifyErrorRef.current(error);
          const shouldTryNewerRevision =
            disposition === 'blocked' && revisionRef.current > revision;

          if (shouldTryNewerRevision) {
            blockedErrorDispositionRef.current = null;
            flushAfterCurrentRef.current = true;
            setSaveErrorDisposition('retryable');
          } else {
            blockedErrorDispositionRef.current =
              disposition === 'retryable' ? null : disposition;
            setSaveErrorDisposition(disposition);
          }
          setLastError(error);
        }
        return false;
      } finally {
        if (generation === generationRef.current) {
          const shouldFlushAgain =
            flushAfterCurrentRef.current &&
            enabledRef.current &&
            !blockedErrorDispositionRef.current &&
            !pausedRef.current &&
            revisionRef.current > lastSavedRevisionRef.current;

          flushAfterCurrentRef.current = false;
          inFlightRef.current = null;
          savingRevisionRef.current = null;
          setIsSaving(false);

          if (shouldFlushAgain) {
            queueMicrotask(() => {
              void flushNow();
            });
          }
        }
      }
    })();

    inFlightRef.current = savePromise;
    return savePromise;
  }, []);

  const markDirty = useCallback(() => {
    revisionRef.current += 1;
    setSaveStatus('saving');

    if (blockedErrorDispositionRef.current === 'blocked') {
      blockedErrorDispositionRef.current = null;
      setSaveErrorDisposition('retryable');
    }
  }, []);

  const pauseAndDrain = useCallback(async () => {
    pausedRef.current = true;
    flushAfterCurrentRef.current = false;

    const pendingSave = inFlightRef.current;
    if (pendingSave) {
      await pendingSave;
    }
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
  }, []);

  useEffect(() => {
    if (!enabled || !sessionKey) {
      return;
    }

    const intervalId = window.setInterval(() => {
      void flushNow();
    }, intervalMs);

    return () => window.clearInterval(intervalId);
  }, [enabled, flushNow, intervalMs, sessionKey]);

  useEffect(
    () => () => {
      generationRef.current += 1;
      inFlightRef.current = null;
    },
    []
  );

  return {
    flushNow,
    hasSaveError:
      saveErrorDisposition === 'retryable' ||
      saveErrorDisposition === 'blocked',
    isSaving,
    lastError,
    markDirty,
    pauseAndDrain,
    resume,
    saveErrorDisposition,
    saveStatus
  };
}
